import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';

interface User {
  id?: string;
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Inactive';
  walletAddress: string;
}

interface UserFormProps {
  userId?: string;
}

export const UserForm = ({ userId }: UserFormProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User>({
    name: '',
    email: '',
    role: 'Viewer',
    status: 'Active',
    walletAddress: '',
  });
  const [loading, setLoading] = useState(false);

  const isEditing = Boolean(userId);

  useEffect(() => {
    if (isEditing && userId) {
      // Simulate loading existing user data
      setLoading(true);
      setTimeout(() => {
        setUser({
          id: userId,
          name: 'John Doe',
          email: 'john@example.com',
          role: 'Admin',
          status: 'Active',
          walletAddress: '0x742b7227f6d3a5cF7Ba3E6e39C7E6b4eF8c7A1D0',
        });
        setLoading(false);
      }, 500);
    }
  }, [userId, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success(isEditing ? 'User updated successfully!' : 'User created successfully!');
      navigate('/users');
    } catch (error) {
      toast.error('Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof User, value: string) => {
    setUser(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/users')}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {isEditing ? 'Edit User' : 'Create New User'}
              </h1>
              <p className="text-muted-foreground">
                {isEditing ? 'Update user information and permissions' : 'Add a new user to the system'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={user.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter full name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={user.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter email address"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Role *
                </label>
                <select
                  value={user.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="Admin">Admin</option>
                  <option value="Editor">Editor</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Status
                </label>
                <select
                  value={user.status}
                  onChange={(e) => handleInputChange('status', e.target.value as 'Active' | 'Inactive')}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-foreground">
                  Wallet Address
                </label>
                <input
                  type="text"
                  value={user.walletAddress}
                  onChange={(e) => handleInputChange('walletAddress', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="0x..."
                  pattern="^0x[a-fA-F0-9]{40}$"
                />
                <p className="text-xs text-muted-foreground">
                  Enter a valid Ethereum wallet address (optional)
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {loading ? 'Saving...' : isEditing ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};